<template>
  <transition
    name="dialog-fade"
    @after-enter="afterEnter"
    @after-leave="afterLeave"
  >
    <div
      v-show="visible"
      class="el-dialog__wrapper"
      @click.self="handleWrapperClick"
    >
      <div
        class="el-dialog custom-dialog"
        :class="{ 'full-screen': isFullScreen }"
        :style="dialogStyle"
      >
        <!-- Header slot or default header -->
        <slot v-if="showHeader" name="header">
          <CustomDialogHeader
            :title="title"
            @max="handleMax"
            @min="handleMin"
            @close="handleClose"
          />
        </slot>

        <!-- Content -->
        <el-scrollbar v-if="rendered" class="custom-dialog-body scroll-bar flex-1" :style="bodyStyle">
          <component
            :is="defaultComp"
            v-bind="innerProps"
            v-on="$listeners"
            @cancel="handleClose"
            @confirm="handleConfirm"
            @max="handleMax"
            @min="handleMin"
            @close="handleClose"
            @toggle="toggleFullScreen"
          />
        </el-scrollbar>
      </div>
    </div>
  </transition>
</template>

<script>
import Popup from "element-ui/src/utils/popup";
import emitter from "element-ui/src/mixins/emitter";
import CustomDialogHeader from "./CustomDialogHeader.vue";

export default {
  name: "CustomDialog",
  mixins: [Popup, emitter],
  components: { CustomDialogHeader },
  props: {
    modal: {
      type: Boolean,
      default: true,
    },
    modalAppendToBody: {
      type: Boolean,
      default: true,
    },
    visible: { type: Boolean, default: false },
    title: { type: String, default: "" },
    Comp: { type: [Object, Function], default: null },
    innerProps: { type: Object, default: () => ({}) },
    showHeader: { type: Boolean, default: true },
    height: { type: String, default: null },
    width: { type: String, default: "80%" },
    zIndex: { type: Number, default: null },
    closeOnClickModal: {
      type: Boolean,
      default: true,
    },
    padding: {
      type: [String, Number, Boolean],
      default: "20px",
    },
  },
  data() {
    return {
      isFullScreen: false,
      dialogTransform: "translate(-50%, -50%)",
      dialogTop: "50%",
      dialogLeft: "50%",
    };
  },
  computed: {
    dialogStyle() {
      return {
        height: this.isFullScreen ? "100vh" : this.height || "auto",
        width: this.isFullScreen ? "100vw" : this.width,
        top: this.isFullScreen ? "0" : this.dialogTop,
        left: this.isFullScreen ? "0" : this.dialogLeft,
        transform: this.isFullScreen ? "translate(0, 0)" : this.dialogTransform,
      };
    },
    bodyStyle() {
      let padding = "20px";
      if (typeof this.padding === "boolean") {
        return {
          padding: this.padding ? "20px" : "0",
        };
      } else if (typeof this.padding === "number") {
        return {
          padding: `${this.padding}px`,
        };
      } else if (typeof this.padding === "string") {
        return {
          padding: this.padding,
        };
      }
      return {
        padding: padding,
      };
    },
    defaultComp() {
      if (this.$slots.default) {
        // 只渲染 slot 的第一个节点（通常是组件），不包裹 div
        return {
          render: () =>
            Array.isArray(this.$slots.default)
              ? this.$slots.default[0]
              : this.$slots.default,
        };
      }
      return this.Comp;
    },
  },
  methods: {
    handleClose() {
      this.$emit("update:visible", false);
    },
    handleConfirm(data) {
      this.$emit("confirm", data);
    },
    toggleFullScreen() {
      if (this.isFullScreen) {
        this.handleMin();
      } else {
        this.handleMax();
      }
    },
    handleMax() {
      console.log("Maximizing dialog");
      this.isFullScreen = true;
      this.$nextTick(() => {
        window.dispatchEvent(new Event("resize"));
      });
    },
    handleMin() {
      console.log("Minimizing dialog");
      this.isFullScreen = false;
      this.$nextTick(() => {
        window.dispatchEvent(new Event("resize"));
      });
    },
    updatePopper() {
      this.broadcast("ElSelectDropdown", "updatePopper");
      this.broadcast("ElDropdownMenu", "updatePopper");
    },
    afterEnter() {
      // 动态调整弹窗垂直居中或顶部对齐
      this.$nextTick(() => {
        const dialog = this.$el.querySelector(".custom-dialog");
        if (dialog) {
          const winH = window.innerHeight;
          const dialogH = dialog.offsetHeight;
          if (dialogH > winH) {
            this.dialogTransform = "translateX(-50%)";
            this.dialogTop = "0";
          } else {
            this.dialogTransform = "translate(-50%, -50%)";
            this.dialogTop = "50%";
          }
        }
      });
      this.$emit("opened");
    },
    afterLeave() {
      this.$emit("closed");
    },
    handleWrapperClick() {
      if (!this.closeOnClickModal) return;
      this.handleClose();
    },
  },
  mounted() {
    if (this.visible) {
      this.rendered = true;
      this.open();
      if (this.appendToBody) {
        document.body.appendChild(this.$el);
      }
    }
  },
  destroyed() {
    // if appendToBody is true, remove DOM node after destroy
    if (this.appendToBody && this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/element-variables.scss";

.custom-dialog {
  position: absolute;
  display: flex;
  flex-direction: column;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 8px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.15);
  transition: all 0.2s;
  background-color: $--background-color-base;

  &.full-screen {
    top: 0;
    left: 0;
    border-radius: 0;
    transform: translate(0, 0);
    width: 100vw !important;
    height: 100vh !important;
  }
}

.custom-dialog-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}
</style>
